import { useId, type ReactNode } from 'react';
import { Badge, type BadgeType } from '../../../atoms/Badge/Badge';
import { Icon } from '../../../atoms/Icon/Icon';
import { Text } from '../../../atoms/Text/Text';
import { Card } from '../../../molecules/Card/Card';
import { cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';
import styles from './InsightListCard.module.css';

export type InsightItem = {
  /** Stable per item, for React. */
  id: string;
  /** A short lead-in drawn first and heavier: the metric, the brand, the topic. */
  lead?: string;
  /**
   * An image drawn in place of the lead's text, in a column of its own to the
   * left of the text: a brand's logo (GP, 2026-11-04). The lead stays its
   * accessible name and its tooltip. Once one row has a logo, every row of the
   * list keeps the column, and a row without one shows its lead there.
   */
  logo?: string;
  text: string;
  /** The movement in a word or a figure — `+18.0%`, `−2.6 pts`. */
  badge?: { label: string; tone?: BadgeType };
  /**
   * Where the item comes from on somebody else's site — a post's permalink.
   * Its lead becomes the link, opening in a new tab.
   */
  href?: string;
  /**
   * The row opens what stands behind it — the conversations it counts — through
   * the card's `onOpen`. Not with `href`: a row is one control or the other.
   */
  openable?: boolean;
  /** Which of the card's `groups` the row is listed under. */
  group?: string;
};

/** A heading the card lists rows under, in the order given. */
export type InsightGroup = {
  id: string;
  title: string;
  /** What the group says when no row is in it. */
  emptyLabel: string;
};

export type InsightListView = {
  items: InsightItem[];
};

export type InsightListCardProps = WidgetBlockProps & {
  title: string;
  helpText?: string;
  helpSupportingText?: string;
  view?: InsightListView;
  /** What the card says when nothing qualified — better than "No data available". */
  emptyLabel?: string;
  /** A ranked list numbers its items (Key movements); a set of rows does not. */
  numbered?: boolean;
  /** Cut each item's text after this many lines — quoted posts, which run long. */
  clamp?: 2 | 3;
  /** Opens an `openable` row's conversations, by the row's id. */
  onOpen?: (id: string) => void;
  /**
   * List the rows under these headings, each row by its `group` — Key
   * movements' positive and negative drivers (GP, 2026-11-03). A group with no
   * row keeps its heading and says so. Omit for one list.
   */
  groups?: readonly InsightGroup[];
};

const CLAMP: Record<number, string | undefined> = { 2: styles.clamp2, 3: styles.clamp3 };

/**
 * A card of short written findings, one per row — the executive dashboard's
 * key movements, drivers and operator standings (2026-10-27), and the posts
 * that stand behind them. A row can open the conversations it counts. Not in Figma: it is the md `Card` with a list in its
 * `Chart` slot, the same box, header and states as every chart card, so it
 * sits in a grid row beside them.
 */
export function InsightListCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  title,
  helpText,
  helpSupportingText,
  emptyLabel,
  numbered = false,
  clamp,
  onOpen,
  groups,
}: InsightListCardProps) {
  const groupId = useId();
  const hasData = view !== undefined && view.items.length > 0;
  const state = cardState(status, hasData, onRetry);
  const List = numbered ? 'ol' : 'ul';

  const list = (items: readonly InsightItem[]) => {
    const logos = !numbered && items.some((item) => item.logo);
    return (
      <List className={styles.list}>
        {items.map((item, i) => {
          const row: ReactNode = (
            <>
              {logos && (
                <span className={styles.mark}>
                  {item.logo ? (
                    <img
                      className={styles.logo}
                      src={item.logo}
                      alt={item.lead ?? ''}
                      title={item.lead}
                    />
                  ) : (
                    item.lead && (
                      <Text
                        size="sm"
                        leading="normal"
                        weight="medium"
                        color="primary"
                        className={styles.lead}
                      >
                        {item.lead}
                      </Text>
                    )
                  )}
                </span>
              )}
              {numbered && (
                <span className={styles.marker} aria-hidden="true">
                  <Text size="xs" weight="medium" color="secondary">
                    {i + 1}
                  </Text>
                </span>
              )}
              <span className={styles.body}>
                {!logos &&
                  item.lead &&
                  (item.href ? (
                    <a className={styles.link} href={item.href} target="_blank" rel="noreferrer">
                      <Text size="sm" leading="normal" weight="medium" color="primary">
                        {item.lead}
                      </Text>
                      <Icon name="arrowUpRight" className={styles.linkIcon} />
                    </a>
                  ) : (
                    <Text
                      size="sm"
                      leading="normal"
                      weight="medium"
                      color="primary"
                      className={styles.lead}
                    >
                      {item.lead}
                    </Text>
                  ))}
                <Text size="sm" leading="normal" color="secondary" className={CLAMP[clamp ?? 0]}>
                  {item.text}
                </Text>
              </span>
              {item.badge && (
                <Badge type={item.badge.tone ?? 'gray'} className={styles.badge}>
                  {item.badge.label}
                </Badge>
              )}
            </>
          );
          const layout = logos ? styles.withLogo : numbered ? styles.numbered : styles.item;
          /*
           * A row that opens something is one button, so it has a keyboard
           * stop; its lead takes the link look on hover and focus, as a
           * table row's does (`TableCell` `rowLink`).
           */
          return (
            <li key={item.id}>
              {onOpen && item.openable && !item.href ? (
                <button
                  type="button"
                  className={`${layout} ${styles.open}`}
                  onClick={() => onOpen(item.id)}
                >
                  {row}
                </button>
              ) : (
                <div className={layout}>{row}</div>
              )}
            </li>
          );
        })}
      </List>
    );
  };

  return (
    <Card
      /* Set only when this card is a widget on a custom report — see `CardChrome`. */
      {...chrome}
      className={spanClass(span)}
      {...state}
      emptyLabel={state.emptyLabel ?? emptyLabel}
      title={title}
      helpText={helpText}
      helpSupportingText={helpSupportingText}
      chart={
        view &&
        (groups ? (
          <div className={styles.groups}>
            {groups.map((group) => {
              const items = view.items.filter((item) => item.group === group.id);
              const headingId = `${groupId}-${group.id}`;
              return (
                <div
                  key={group.id}
                  role="group"
                  aria-labelledby={headingId}
                  className={styles.group}
                >
                  <Text id={headingId} size="sm" weight="medium" color="tertiary">
                    {group.title}
                  </Text>
                  {items.length > 0 ? (
                    list(items)
                  ) : (
                    <Text size="sm" leading="normal" color="secondary">
                      {group.emptyLabel}
                    </Text>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          list(view.items)
        ))
      }
    />
  );
}
